'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BusinessPassport {
  legal_name: string;
  trade_name: string;
  entity_type: string;
  pan: string | null;
  gstin: string | null;
  founders: Array<{ full_name: string; email: string; pan: string; is_director: boolean }>;
  registered_offices: Array<{ address_line1: string; address_line2: string; city: string; state: string; pincode: string }>;
  business_activities: Array<{ description: string; nic_code: string; industry: string }>;
  business_passports: { completeness_score: number; msme_number: string | null } | null;
}

interface EligibilityResult {
  eligible: boolean;
  category: 'micro' | 'small' | 'medium' | null;
  reason: string;
  missingFields: string[];
  passportScore: number;
  recommendation: string;
}

interface MSMEFormData {
  // Step 1 – Business Identity
  legalName: string;
  tradeName: string;
  pan: string;
  entityType: string;
  dateOfIncorporation: string;
  // Step 2 – Address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  district: string;
  // Step 3 – Activity & Classification
  majorActivity: string;
  nicCode: string;
  industryDescription: string;
  // Step 4 – Investment & Turnover
  investmentInPlantMachinery: string;
  annualTurnover: string;
  // Step 5 – Employment
  maleEmployees: string;
  femaleEmployees: string;
  // Step 6 – GST & Signatory
  gstin: string;
  gstLinked: boolean;
  signatoryName: string;
  signatoryPan: string;
  signatoryDesignation: string;
  signatoryMobile: string;
  signatoryEmail: string;
}

// ─── Demo data ────────────────────────────────────────────────────────────────

const demoPassport: BusinessPassport = {
  legal_name: 'Elysium Fragrances Private Limited',
  trade_name: 'Elysium',
  entity_type: 'private_limited',
  pan: 'AABCE1234F',
  gstin: null,
  founders: [
    { full_name: 'Arjun Sharma', email: 'arjun@elysiumfragrances.com', pan: 'ABCPS1234D', is_director: true },
    { full_name: 'Priya Mehta', email: 'priya@elysiumfragrances.com', pan: 'XYZPM5678E', is_director: true },
  ],
  registered_offices: [{ address_line1: '42, Sector 18', address_line2: 'Noida Industrial Area', city: 'Noida', state: 'Uttar Pradesh', pincode: '201301' }],
  business_activities: [{ description: 'Manufacture and sale of perfumes, cosmetics, and skincare products', nic_code: '20231', industry: 'Manufacturing - Cosmetics' }],
  business_passports: { completeness_score: 72, msme_number: null },
};

const entityTypeLabels: Record<string, string> = {
  private_limited: 'Private Limited Company',
  llp: 'Limited Liability Partnership',
  opc: 'One Person Company',
  partnership: 'Partnership Firm',
  sole_proprietorship: 'Sole Proprietorship',
};

const STEPS = [
  { id: 1, label: 'Eligibility', icon: '◎' },
  { id: 2, label: 'Business Identity', icon: '◈' },
  { id: 3, label: 'Address', icon: '⬡' },
  { id: 4, label: 'Activity', icon: '◇' },
  { id: 5, label: 'Investment & Turnover', icon: '₹' },
  { id: 6, label: 'Employment', icon: '◉' },
  { id: 7, label: 'GST & Signatory', icon: '✓' },
  { id: 8, label: 'Review & Submit', icon: '→' },
];

// MSME classification thresholds (as per Udyam 2020 criteria)
const MSME_THRESHOLDS = {
  micro:  { investment: 1,   turnover: 5   },
  small:  { investment: 10,  turnover: 50  },
  medium: { investment: 50,  turnover: 250 },
};

function classifyMSME(investmentCr: number, turnoverCr: number): 'micro' | 'small' | 'medium' | null {
  if (investmentCr <= MSME_THRESHOLDS.micro.investment && turnoverCr <= MSME_THRESHOLDS.micro.turnover) return 'micro';
  if (investmentCr <= MSME_THRESHOLDS.small.investment && turnoverCr <= MSME_THRESHOLDS.small.turnover) return 'small';
  if (investmentCr <= MSME_THRESHOLDS.medium.investment && turnoverCr <= MSME_THRESHOLDS.medium.turnover) return 'medium';
  return null;
}

// ─── Auto-fill helper ─────────────────────────────────────────────────────────

function buildFormFromPassport(bp: BusinessPassport): MSMEFormData {
  const office = bp.registered_offices?.[0];
  const activity = bp.business_activities?.[0];
  const director = bp.founders?.find(f => f.is_director) ?? bp.founders?.[0];
  return {
    legalName: bp.legal_name ?? '',
    tradeName: bp.trade_name ?? '',
    pan: bp.pan ?? '',
    entityType: bp.entity_type ?? '',
    dateOfIncorporation: '',
    addressLine1: office?.address_line1 ?? '',
    addressLine2: office?.address_line2 ?? '',
    city: office?.city ?? '',
    state: office?.state ?? '',
    pincode: office?.pincode ?? '',
    district: office?.city ?? '',
    majorActivity: activity?.industry?.includes('Manufactur') ? 'Manufacturing' : 'Services',
    nicCode: activity?.nic_code ?? '',
    industryDescription: activity?.description ?? '',
    investmentInPlantMachinery: '',
    annualTurnover: '',
    maleEmployees: '',
    femaleEmployees: '',
    gstin: bp.gstin ?? '',
    gstLinked: !!bp.gstin,
    signatoryName: director?.full_name ?? '',
    signatoryPan: director?.pan ?? '',
    signatoryDesignation: director?.is_director ? 'Director' : 'Partner',
    signatoryMobile: '',
    signatoryEmail: director?.email ?? '',
  };
}

// ─── Auto-filled badge ────────────────────────────────────────────────────────

function AutoBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-ai bg-ai-light px-1.5 py-0.5 rounded ml-2">
      ◈ Auto-filled
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MSMEPage() {
  const [passport, setPassport] = useState<BusinessPassport>(demoPassport);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'register' | 'fees' | 'docs'>('register');
  const [currentStep, setCurrentStep] = useState(1);
  const [eligibility, setEligibility] = useState<EligibilityResult | null>(null);
  const [form, setForm] = useState<MSMEFormData>(buildFormFromPassport(demoPassport));
  const [autoFilledFields] = useState<Set<string>>(new Set([
    'legalName', 'tradeName', 'pan', 'entityType',
    'addressLine1', 'addressLine2', 'city', 'state', 'pincode', 'district',
    'majorActivity', 'nicCode', 'industryDescription',
    'signatoryName', 'signatoryPan', 'signatoryDesignation', 'signatoryEmail',
  ]));
  const [submitted, setSubmitted] = useState(false);
  const [gstLinkStatus, setGstLinkStatus] = useState<'idle' | 'linking' | 'linked' | 'error'>('idle');

  // Eligibility inputs
  const [turnoverInput, setTurnoverInput] = useState('');
  const [investmentInput, setInvestmentInput] = useState('');
  const [eligibilityChecked, setEligibilityChecked] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        const { data } = await supabase
          .from('businesses')
          .select('*, founders(*), registered_offices(*), business_activities(*), business_passports(*)')
          .eq('user_id', user.id)
          .limit(1)
          .single();
        if (data) {
          setPassport(data as BusinessPassport);
          setForm(buildFormFromPassport(data as BusinessPassport));
        }
      } catch { /* use demo */ }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function checkEligibility() {
    const inv = parseFloat(investmentInput) || 0;
    const tur = parseFloat(turnoverInput) || 0;
    const category = classifyMSME(inv, tur);
    const missing: string[] = [];
    if (!passport.pan) missing.push('PAN');
    if (!passport.registered_offices?.length) missing.push('Registered Address');
    if (!passport.business_activities?.length) missing.push('Business Activity');
    const score = passport.business_passports?.completeness_score ?? 0;

    if (inv === 0 || tur === 0) {
      setEligibility({
        eligible: false,
        category: null,
        reason: 'Please enter investment and annual turnover to check eligibility.',
        missingFields: missing,
        passportScore: score,
        recommendation: 'Enter your financial details above.',
      });
    } else if (!category) {
      setEligibility({
        eligible: false,
        category: null,
        reason: `Your business exceeds MSME limits (Investment > ₹50 Cr OR Turnover > ₹250 Cr).`,
        missingFields: missing,
        passportScore: score,
        recommendation: 'Your business does not qualify as MSME under Udyam criteria.',
      });
    } else {
      setEligibility({
        eligible: true,
        category,
        reason: `Qualifies as ${category.toUpperCase()} enterprise under Udyam Registration criteria.`,
        missingFields: missing,
        passportScore: score,
        recommendation: missing.length > 0
          ? `Complete Business Passport (${missing.join(', ')}) before filing.`
          : 'Business Passport is sufficiently complete. Proceed to registration.',
      });
      setForm(f => ({ ...f, investmentInPlantMachinery: investmentInput, annualTurnover: turnoverInput }));
    }
    setEligibilityChecked(true);
  }

  function handleGSTLink() {
    if (!form.gstin) return;
    setGstLinkStatus('linking');
    setTimeout(() => {
      setGstLinkStatus('linked');
      setForm(f => ({ ...f, gstLinked: true }));
    }, 1500);
  }

  function updateField(key: keyof MSMEFormData, value: string | boolean) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function isAutoFilled(field: string) {
    return autoFilledFields.has(field) && (form as Record<string, unknown>)[field];
  }

  const totalEmployees = (parseInt(form.maleEmployees) || 0) + (parseInt(form.femaleEmployees) || 0);

  // ─── Render helpers ──────────────────────────────────────────────────────────

  function renderEligibilityStep() {
    return (
      <div className="space-y-6">
        {/* Passport completeness banner */}
        <div className="bg-ai-light border border-ai/20 rounded-xl p-4 flex items-start gap-3">
          <span className="text-ai text-lg mt-0.5">◈</span>
          <div>
            <p className="text-[13px] font-semibold text-ai">Business Passport Connected</p>
            <p className="text-[12px] text-ink-muted mt-0.5">
              {passport.business_passports?.completeness_score ?? 0}% complete — {passport.legal_name}. Key fields will be auto-filled in the registration form.
            </p>
          </div>
          <div className="ml-auto">
            <div className="w-12 h-12 rounded-full border-2 border-ai flex items-center justify-center">
              <span className="text-[13px] font-bold text-ai">{passport.business_passports?.completeness_score ?? 0}%</span>
            </div>
          </div>
        </div>

        {/* Udyam classification guide */}
        <div>
          <h3 className="text-[13px] font-semibold text-ink mb-3">Udyam Classification Thresholds</h3>
          <div className="grid grid-cols-3 gap-3">
            {(['micro', 'small', 'medium'] as const).map(cat => (
              <div key={cat} className={`rounded-xl border p-3 ${eligibility?.category === cat ? 'border-ai bg-ai-light' : 'border-border bg-surface-2'}`}>
                <p className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${eligibility?.category === cat ? 'text-ai' : 'text-ink-muted'}`}>{cat}</p>
                <p className="text-[12px] text-ink">Investment ≤ ₹{MSME_THRESHOLDS[cat].investment} Cr</p>
                <p className="text-[12px] text-ink-muted">Turnover ≤ ₹{MSME_THRESHOLDS[cat].turnover} Cr</p>
              </div>
            ))}
          </div>
        </div>

        {/* Eligibility inputs */}
        <div className="bg-surface-2 rounded-xl border border-border p-5 space-y-4">
          <h3 className="text-[13px] font-semibold text-ink">Check Your Eligibility</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
                Investment in Plant & Machinery (₹ Crore)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={investmentInput}
                onChange={e => setInvestmentInput(e.target.value)}
                placeholder="e.g. 0.5"
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
                Annual Turnover (₹ Crore)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={turnoverInput}
                onChange={e => setTurnoverInput(e.target.value)}
                placeholder="e.g. 2.5"
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai"
              />
            </div>
          </div>
          <button
            onClick={checkEligibility}
            className="w-full py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors"
          >
            Check Eligibility
          </button>
        </div>

        {/* Result */}
        {eligibility && (
          <div className={`rounded-xl border p-4 ${eligibility.eligible ? 'bg-success/5 border-success/30' : 'bg-error/5 border-error/30'}`}>
            <div className="flex items-start gap-3">
              <span className={`text-xl ${eligibility.eligible ? 'text-success' : 'text-error'}`}>
                {eligibility.eligible ? '✓' : '✕'}
              </span>
              <div className="flex-1">
                <p className={`text-[13px] font-semibold ${eligibility.eligible ? 'text-success' : 'text-error'}`}>
                  {eligibility.eligible ? `Eligible — ${eligibility.category?.toUpperCase()} Enterprise` : 'Not Eligible'}
                </p>
                <p className="text-[12px] text-ink-muted mt-1">{eligibility.reason}</p>
                {eligibility.recommendation && (
                  <p className="text-[12px] text-ink mt-1.5 font-medium">{eligibility.recommendation}</p>
                )}
                {eligibility.missingFields.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {eligibility.missingFields.map(f => (
                      <span key={f} className="text-[11px] bg-warning/10 text-warning border border-warning/20 px-2 py-0.5 rounded-full">
                        Missing: {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {eligibility?.eligible && (
          <button
            onClick={() => setCurrentStep(2)}
            className="w-full py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors"
          >
            Proceed to Registration →
          </button>
        )}
      </div>
    );
  }

  function renderBusinessIdentityStep() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Legal Name {isAutoFilled('legalName') && <AutoBadge />}
            </label>
            <input value={form.legalName} onChange={e => updateField('legalName', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Trade / Brand Name {isAutoFilled('tradeName') && <AutoBadge />}
            </label>
            <input value={form.tradeName} onChange={e => updateField('tradeName', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              PAN {isAutoFilled('pan') && <AutoBadge />}
            </label>
            <input value={form.pan} onChange={e => updateField('pan', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai font-mono" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Entity Type {isAutoFilled('entityType') && <AutoBadge />}
            </label>
            <select value={form.entityType} onChange={e => updateField('entityType', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai">
              {Object.entries(entityTypeLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">Date of Incorporation / Commencement</label>
            <input type="date" value={form.dateOfIncorporation} onChange={e => updateField('dateOfIncorporation', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
        </div>
      </div>
    );
  }

  function renderAddressStep() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Address Line 1 {isAutoFilled('addressLine1') && <AutoBadge />}
            </label>
            <input value={form.addressLine1} onChange={e => updateField('addressLine1', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div className="col-span-2">
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Address Line 2 {isAutoFilled('addressLine2') && <AutoBadge />}
            </label>
            <input value={form.addressLine2} onChange={e => updateField('addressLine2', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              City {isAutoFilled('city') && <AutoBadge />}
            </label>
            <input value={form.city} onChange={e => updateField('city', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              District {isAutoFilled('district') && <AutoBadge />}
            </label>
            <input value={form.district} onChange={e => updateField('district', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              State {isAutoFilled('state') && <AutoBadge />}
            </label>
            <input value={form.state} onChange={e => updateField('state', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Pincode {isAutoFilled('pincode') && <AutoBadge />}
            </label>
            <input value={form.pincode} onChange={e => updateField('pincode', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai font-mono" />
          </div>
        </div>
      </div>
    );
  }

  function renderActivityStep() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Major Activity {isAutoFilled('majorActivity') && <AutoBadge />}
            </label>
            <select value={form.majorActivity} onChange={e => updateField('majorActivity', e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai">
              <option value="Manufacturing">Manufacturing</option>
              <option value="Services">Services</option>
              <option value="Trading">Trading</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              NIC Code {isAutoFilled('nicCode') && <AutoBadge />}
            </label>
            <input value={form.nicCode} onChange={e => updateField('nicCode', e.target.value)}
              placeholder="e.g. 20231"
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai font-mono" />
          </div>
          <div className="col-span-2">
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Industry / Activity Description {isAutoFilled('industryDescription') && <AutoBadge />}
            </label>
            <textarea value={form.industryDescription} onChange={e => updateField('industryDescription', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai resize-none" />
          </div>
        </div>
        <div className="bg-surface-2 rounded-lg border border-border p-3">
          <p className="text-[11px] text-ink-muted">
            <span className="font-semibold text-ink">NIC Code</span> — National Industrial Classification code for your primary business activity. 
            Manufacturing codes start with 10–33; Service codes with 45–99.
          </p>
        </div>
      </div>
    );
  }

  function renderInvestmentStep() {
    const inv = parseFloat(form.investmentInPlantMachinery) || 0;
    const tur = parseFloat(form.annualTurnover) || 0;
    const cat = classifyMSME(inv, tur);

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Investment in Plant & Machinery / Equipment (₹ Crore)
              {form.investmentInPlantMachinery && <AutoBadge />}
            </label>
            <input type="number" min="0" step="0.01" value={form.investmentInPlantMachinery}
              onChange={e => updateField('investmentInPlantMachinery', e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
              Annual Turnover (₹ Crore)
              {form.annualTurnover && <AutoBadge />}
            </label>
            <input type="number" min="0" step="0.01" value={form.annualTurnover}
              onChange={e => updateField('annualTurnover', e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
        </div>

        {/* Live classification */}
        {(inv > 0 || tur > 0) && (
          <div className={`rounded-xl border p-4 ${cat ? 'bg-success/5 border-success/30' : 'bg-error/5 border-error/30'}`}>
            <p className={`text-[13px] font-semibold ${cat ? 'text-success' : 'text-error'}`}>
              {cat ? `Classification: ${cat.toUpperCase()} Enterprise` : 'Exceeds MSME Limits'}
            </p>
            {cat && (
              <p className="text-[12px] text-ink-muted mt-1">
                Investment ≤ ₹{MSME_THRESHOLDS[cat].investment} Cr &amp; Turnover ≤ ₹{MSME_THRESHOLDS[cat].turnover} Cr
              </p>
            )}
          </div>
        )}

        {/* Threshold reference */}
        <div className="bg-surface-2 rounded-xl border border-border p-4">
          <p className="text-[12px] font-semibold text-ink mb-3">Udyam 2020 Thresholds (both criteria must be met)</p>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-ink-muted">
                <th className="text-left pb-2 font-medium">Category</th>
                <th className="text-right pb-2 font-medium">Investment</th>
                <th className="text-right pb-2 font-medium">Turnover</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(['micro', 'small', 'medium'] as const).map(c => (
                <tr key={c} className={cat === c ? 'text-ai font-semibold' : 'text-ink'}>
                  <td className="py-1.5 capitalize">{c}</td>
                  <td className="text-right py-1.5">≤ ₹{MSME_THRESHOLDS[c].investment} Cr</td>
                  <td className="text-right py-1.5">≤ ₹{MSME_THRESHOLDS[c].turnover} Cr</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderEmploymentStep() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">Male Employees</label>
            <input type="number" min="0" value={form.maleEmployees}
              onChange={e => updateField('maleEmployees', e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">Female Employees</label>
            <input type="number" min="0" value={form.femaleEmployees}
              onChange={e => updateField('femaleEmployees', e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
          </div>
        </div>
        {totalEmployees > 0 && (
          <div className="bg-ai-light border border-ai/20 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-[13px] font-semibold text-ai">Total Workforce</p>
              <p className="text-[12px] text-ink-muted mt-0.5">
                {form.maleEmployees || 0} male + {form.femaleEmployees || 0} female
              </p>
            </div>
            <div className="text-3xl font-bold text-ai">{totalEmployees}</div>
          </div>
        )}
        <div className="bg-surface-2 rounded-lg border border-border p-3">
          <p className="text-[11px] text-ink-muted">
            Employment data is collected for statistical purposes. It does not affect your MSME classification, which is based solely on investment and turnover.
          </p>
        </div>
      </div>
    );
  }

  function renderGSTSignatoryStep() {
    return (
      <div className="space-y-5">
        {/* GST Auto-Link */}
        <div className="bg-surface-2 rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[13px] font-semibold text-ink">GST Auto-Link</p>
              <p className="text-[12px] text-ink-muted mt-0.5">Link your GSTIN to auto-populate GST details in Udyam portal</p>
            </div>
            {form.gstLinked && (
              <span className="text-[11px] font-semibold text-success bg-success/10 border border-success/20 px-2 py-1 rounded-full">✓ Linked</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              value={form.gstin}
              onChange={e => updateField('gstin', e.target.value)}
              placeholder="Enter GSTIN (optional)"
              className="flex-1 px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai font-mono"
            />
            <button
              onClick={handleGSTLink}
              disabled={!form.gstin || gstLinkStatus === 'linking' || gstLinkStatus === 'linked'}
              className="px-4 py-2 text-[12px] font-semibold bg-ai text-white rounded-lg hover:bg-ai-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {gstLinkStatus === 'linking' ? 'Linking…' : gstLinkStatus === 'linked' ? '✓ Linked' : 'Link GST'}
            </button>
          </div>
          {!form.gstin && (
            <p className="text-[11px] text-ink-muted mt-2">
              GSTIN is optional for Udyam registration. If registered, linking enables auto-verification of turnover data.
            </p>
          )}
        </div>

        {/* Authorized Signatory */}
        <div>
          <p className="text-[13px] font-semibold text-ink mb-3">Authorized Signatory</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
                Full Name {isAutoFilled('signatoryName') && <AutoBadge />}
              </label>
              <input value={form.signatoryName} onChange={e => updateField('signatoryName', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
                PAN {isAutoFilled('signatoryPan') && <AutoBadge />}
              </label>
              <input value={form.signatoryPan} onChange={e => updateField('signatoryPan', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai font-mono" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
                Designation {isAutoFilled('signatoryDesignation') && <AutoBadge />}
              </label>
              <input value={form.signatoryDesignation} onChange={e => updateField('signatoryDesignation', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">Mobile</label>
              <input value={form.signatoryMobile} onChange={e => updateField('signatoryMobile', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
            </div>
            <div className="col-span-2">
              <label className="block text-[12px] font-medium text-ink-muted mb-1.5">
                Email {isAutoFilled('signatoryEmail') && <AutoBadge />}
              </label>
              <input type="email" value={form.signatoryEmail} onChange={e => updateField('signatoryEmail', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-white border border-border rounded-lg focus:outline-none focus:border-ai" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderReviewStep() {
    const inv = parseFloat(form.investmentInPlantMachinery) || 0;
    const tur = parseFloat(form.annualTurnover) || 0;
    const cat = classifyMSME(inv, tur);

    const sections = [
      {
        title: 'Business Identity',
        fields: [
          { label: 'Legal Name', value: form.legalName },
          { label: 'Trade Name', value: form.tradeName },
          { label: 'PAN', value: form.pan },
          { label: 'Entity Type', value: entityTypeLabels[form.entityType] ?? form.entityType },
        ],
      },
      {
        title: 'Address',
        fields: [
          { label: 'Address', value: [form.addressLine1, form.addressLine2].filter(Boolean).join(', ') },
          { label: 'City / District', value: `${form.city}, ${form.district}` },
          { label: 'State', value: form.state },
          { label: 'Pincode', value: form.pincode },
        ],
      },
      {
        title: 'Activity',
        fields: [
          { label: 'Major Activity', value: form.majorActivity },
          { label: 'NIC Code', value: form.nicCode },
          { label: 'Description', value: form.industryDescription },
        ],
      },
      {
        title: 'Investment & Turnover',
        fields: [
          { label: 'Investment', value: form.investmentInPlantMachinery ? `₹${form.investmentInPlantMachinery} Cr` : '—' },
          { label: 'Annual Turnover', value: form.annualTurnover ? `₹${form.annualTurnover} Cr` : '—' },
          { label: 'Classification', value: cat ? cat.toUpperCase() : 'Not classified' },
        ],
      },
      {
        title: 'Employment',
        fields: [
          { label: 'Male Employees', value: form.maleEmployees || '0' },
          { label: 'Female Employees', value: form.femaleEmployees || '0' },
          { label: 'Total', value: String(totalEmployees) },
        ],
      },
      {
        title: 'GST & Signatory',
        fields: [
          { label: 'GSTIN', value: form.gstin || 'Not provided' },
          { label: 'GST Linked', value: form.gstLinked ? 'Yes' : 'No' },
          { label: 'Signatory', value: form.signatoryName },
          { label: 'Designation', value: form.signatoryDesignation },
          { label: 'Email', value: form.signatoryEmail },
        ],
      },
    ];

    return (
      <div className="space-y-4">
        {cat && (
          <div className="bg-success/5 border border-success/30 rounded-xl p-4 flex items-center gap-3">
            <span className="text-success text-xl">✓</span>
            <div>
              <p className="text-[13px] font-semibold text-success">Ready to Submit — {cat.toUpperCase()} Enterprise</p>
              <p className="text-[12px] text-ink-muted mt-0.5">
                Investment ₹{form.investmentInPlantMachinery} Cr · Turnover ₹{form.annualTurnover} Cr
              </p>
            </div>
          </div>
        )}

        {sections.map(sec => (
          <div key={sec.title} className="bg-surface-2 rounded-xl border border-border p-4">
            <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-3">{sec.title}</p>
            <div className="space-y-2">
              {sec.fields.map(f => (
                <div key={f.label} className="flex justify-between text-[13px]">
                  <span className="text-ink-muted">{f.label}</span>
                  <span className="text-ink font-medium text-right max-w-[60%] truncate">{f.value || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
          <p className="text-[12px] text-warning font-semibold mb-1">Important — Self Declaration</p>
          <p className="text-[12px] text-ink-muted">
            By submitting, you declare that the information provided is true and correct. 
            Udyam Registration is free of cost on the official portal (udyamregistration.gov.in). 
            OTP verification via Aadhaar is required for final submission on the government portal.
          </p>
        </div>

        <button
          onClick={() => setSubmitted(true)}
          className="w-full py-3 bg-ai text-white text-[14px] font-semibold rounded-xl hover:bg-ai-dark transition-colors"
        >
          Submit Udyam Registration Application
        </button>
      </div>
    );
  }

  function renderStepContent() {
    switch (currentStep) {
      case 1: return renderEligibilityStep();
      case 2: return renderBusinessIdentityStep();
      case 3: return renderAddressStep();
      case 4: return renderActivityStep();
      case 5: return renderInvestmentStep();
      case 6: return renderEmploymentStep();
      case 7: return renderGSTSignatoryStep();
      case 8: return renderReviewStep();
      default: return null;
    }
  }

  // ─── Fees tab ────────────────────────────────────────────────────────────────

  function renderFeesTab() {
    return (
      <div className="space-y-5">
        <div className="bg-success/5 border border-success/30 rounded-xl p-5 flex items-start gap-4">
          <span className="text-success text-2xl">₹0</span>
          <div>
            <p className="text-[14px] font-bold text-success">Government Fee — FREE</p>
            <p className="text-[12px] text-ink-muted mt-1">
              Udyam Registration is completely free of charge on the official government portal (udyamregistration.gov.in). 
              No government fee is applicable.
            </p>
          </div>
        </div>

        <div className="bg-surface-2 rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-[13px] font-semibold text-ink">Associated Costs (Optional)</p>
          </div>
          <div className="divide-y divide-border">
            {[
              { item: 'Government Portal Fee', cost: '₹0', note: 'Completely free' },
              { item: 'DSC (Digital Signature Certificate)', cost: '₹1,000–₹2,500', note: 'Not required for Udyam; Aadhaar OTP suffices' },
              { item: 'Professional Assistance', cost: '₹500–₹2,000', note: 'Optional — CA/CS filing support' },
              { item: 'Udyam Certificate Printing', cost: '₹0', note: 'Download free from portal' },
            ].map(row => (
              <div key={row.item} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-[13px] text-ink">{row.item}</p>
                  <p className="text-[11px] text-ink-muted mt-0.5">{row.note}</p>
                </div>
                <span className={`text-[13px] font-semibold ${row.cost === '₹0' ? 'text-success' : 'text-ink'}`}>{row.cost}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface-2 rounded-xl border border-border p-4">
          <p className="text-[13px] font-semibold text-ink mb-3">Benefits of Udyam Registration</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              'Priority sector lending from banks',
              'Collateral-free loans under CGTMSE',
              'Subsidy on patent/trademark registration',
              'Protection against delayed payments',
              'Government tender preferences',
              'Reduced electricity tariffs',
              'ISO certification reimbursement',
              'NSIC performance & credit ratings',
            ].map(b => (
              <div key={b} className="flex items-start gap-2">
                <span className="text-success text-[11px] mt-0.5">✓</span>
                <span className="text-[12px] text-ink-muted">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Docs tab ────────────────────────────────────────────────────────────────

  function renderDocsTab() {
    const entityType = form.entityType || passport.entity_type;
    const isCompany = ['private_limited', 'llp', 'opc'].includes(entityType);

    const docs = [
      { name: 'Aadhaar Card of Proprietor/Director', required: true, fromPassport: false },
      { name: 'PAN Card of Business / Proprietor', required: true, fromPassport: !!passport.pan },
      { name: 'Business Address Proof', required: true, fromPassport: !!passport.registered_offices?.length },
      { name: 'Bank Account Details', required: true, fromPassport: false },
      { name: 'GSTIN (if registered)', required: false, fromPassport: !!passport.gstin },
      ...(isCompany ? [
        { name: 'Certificate of Incorporation / LLP Agreement', required: true, fromPassport: false },
        { name: 'MOA & AOA (for Pvt Ltd)', required: entityType === 'private_limited', fromPassport: false },
      ] : []),
      { name: 'Partnership Deed (if applicable)', required: entityType === 'partnership', fromPassport: false },
    ];

    return (
      <div className="space-y-5">
        <div className="bg-ai-light border border-ai/20 rounded-xl p-4">
          <p className="text-[13px] font-semibold text-ai mb-1">Aadhaar-Based Self-Registration</p>
          <p className="text-[12px] text-ink-muted">
            Udyam Registration uses Aadhaar OTP for identity verification. No physical documents need to be uploaded — 
            data is auto-fetched from GSTN and Income Tax databases.
          </p>
        </div>

        <div className="bg-surface-2 rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-[13px] font-semibold text-ink">Documents Checklist</p>
            <p className="text-[11px] text-ink-muted mt-0.5">Based on entity type: {entityTypeLabels[entityType] ?? entityType}</p>
          </div>
          <div className="divide-y divide-border">
            {docs.map(doc => (
              <div key={doc.name} className="px-4 py-3 flex items-center gap-3">
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                  doc.fromPassport ? 'border-success bg-success/10 text-success' : 'border-border text-ink-muted'
                }`}>
                  {doc.fromPassport ? '✓' : '○'}
                </span>
                <div className="flex-1">
                  <p className="text-[13px] text-ink">{doc.name}</p>
                  {doc.fromPassport && (
                    <p className="text-[11px] text-ai mt-0.5">◈ Available in Business Passport</p>
                  )}
                </div>
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                  doc.required ? 'bg-error/10 text-error' : 'bg-surface-3 text-ink-muted'
                }`}>
                  {doc.required ? 'Required' : 'Optional'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface-2 rounded-xl border border-border p-4">
          <p className="text-[13px] font-semibold text-ink mb-2">Registration Timeline</p>
          <div className="space-y-2">
            {[
              { phase: 'Form Filling', time: '15–30 min', note: 'Auto-filled from Business Passport' },
              { phase: 'Aadhaar OTP Verification', time: '2–5 min', note: 'Real-time on government portal' },
              { phase: 'Certificate Generation', time: 'Instant', note: 'Udyam Registration Number issued immediately' },
            ].map(p => (
              <div key={p.phase} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                <div>
                  <p className="text-[13px] text-ink">{p.phase}</p>
                  <p className="text-[11px] text-ink-muted">{p.note}</p>
                </div>
                <span className="text-[12px] font-semibold text-ai">{p.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Success screen ───────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface flex">
        <AppNav variant="app" activePath="/msme" />
        <main className="flex-1 sm:ml-60 pt-14 sm:pt-0 flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 bg-success/10 border-2 border-success/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-success text-3xl">✓</span>
            </div>
            <h2 className="text-[22px] font-bold text-ink mb-2">Application Submitted</h2>
            <p className="text-[14px] text-ink-muted mb-2">
              Your Udyam Registration application has been prepared and is ready for final submission on the government portal.
            </p>
            <div className="bg-surface-2 rounded-xl border border-border p-4 text-left mb-6 space-y-2">
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Business</span>
                <span className="text-ink font-medium">{form.legalName}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Classification</span>
                <span className="text-ai font-semibold uppercase">{classifyMSME(parseFloat(form.investmentInPlantMachinery)||0, parseFloat(form.annualTurnover)||0) ?? '—'}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">GST Linked</span>
                <span className={form.gstLinked ? 'text-success font-medium' : 'text-ink-muted'}>
                  {form.gstLinked ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            <a
              href="https://udyamregistration.gov.in"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 bg-ai text-white text-[14px] font-semibold rounded-xl hover:bg-ai-dark transition-colors mb-3"
            >
              Complete on Udyam Portal →
            </a>
            <button
              onClick={() => { setSubmitted(false); setCurrentStep(1); }}
              className="text-[13px] text-ink-muted hover:text-ink transition-colors"
            >
              Start over
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ─── Main layout ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-surface flex">
      <AppNav variant="app" activePath="/msme" />
      <main className="flex-1 sm:ml-60 pt-14 sm:pt-0">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8">

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Link href="/dashboard" className="text-[12px] text-ink-muted hover:text-ink transition-colors">Dashboard</Link>
                <span className="text-ink-muted text-[12px]">/</span>
                <span className="text-[12px] text-ink">MSME / Udyam Registration</span>
              </div>
              <h1 className="text-[22px] font-bold text-ink">MSME / Udyam Registration</h1>
              <p className="text-[13px] text-ink-muted mt-1">
                Register under the Micro, Small & Medium Enterprises Development Act · Free · Instant Certificate
              </p>
            </div>
            {passport.business_passports?.msme_number && (
              <div className="bg-success/10 border border-success/20 rounded-xl px-4 py-2 text-right">
                <p className="text-[11px] text-success font-semibold uppercase tracking-wide">Registered</p>
                <p className="text-[13px] font-mono font-bold text-ink">{passport.business_passports.msme_number}</p>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-surface-2 rounded-xl p-1 mb-6 border border-border w-fit">
            {[
              { id: 'register', label: 'Register' },
              { id: 'fees', label: 'Fee Structure' },
              { id: 'docs', label: 'Documents' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-4 py-2 text-[13px] font-medium rounded-lg transition-all ${
                  activeTab === tab.id ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'fees' && renderFeesTab()}
          {activeTab === 'docs' && renderDocsTab()}

          {activeTab === 'register' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Step sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-surface-2 rounded-xl border border-border p-3 space-y-1">
                  {STEPS.map(step => (
                    <button
                      key={step.id}
                      onClick={() => {
                        if (step.id === 1 || eligibilityChecked) setCurrentStep(step.id);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all ${
                        currentStep === step.id
                          ? 'bg-ai-light text-ai'
                          : step.id < currentStep
                          ? 'text-success hover:bg-surface-3' :'text-ink-muted hover:bg-surface-3'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                        step.id < currentStep
                          ? 'border-success bg-success/10 text-success'
                          : currentStep === step.id
                          ? 'border-ai bg-ai text-white' :'border-border text-ink-muted'
                      }`}>
                        {step.id < currentStep ? '✓' : step.id}
                      </span>
                      <span className="text-[12px] font-medium">{step.label}</span>
                    </button>
                  ))}
                </div>

                {/* Passport completeness */}
                <div className="mt-4 bg-surface-2 rounded-xl border border-border p-3">
                  <p className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Passport Score</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-ai rounded-full transition-all"
                        style={{ width: `${passport.business_passports?.completeness_score ?? 0}%` }}
                      />
                    </div>
                    <span className="text-[12px] font-semibold text-ai">{passport.business_passports?.completeness_score ?? 0}%</span>
                  </div>
                  <p className="text-[11px] text-ink-muted mt-1.5">
                    {(passport.business_passports?.completeness_score ?? 0) >= 70
                      ? 'Good — most fields auto-filled' :'Complete passport for better auto-fill'}
                  </p>
                </div>
              </div>

              {/* Form area */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl border border-border p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-[16px] font-bold text-ink">
                        Step {currentStep}: {STEPS[currentStep - 1]?.label}
                      </h2>
                      <p className="text-[12px] text-ink-muted mt-0.5">
                        {currentStep === 1 && 'Verify your MSME eligibility before proceeding'}
                        {currentStep === 2 && 'Core business identity details'}
                        {currentStep === 3 && 'Principal place of business address'}
                        {currentStep === 4 && 'Primary business activity and NIC classification'}
                        {currentStep === 5 && 'Financial data for MSME classification'}
                        {currentStep === 6 && 'Workforce headcount (for statistical purposes)'}
                        {currentStep === 7 && 'GST linkage and authorized signatory details'}
                        {currentStep === 8 && 'Review all details before final submission'}
                      </p>
                    </div>
                    <div className="text-[12px] text-ink-muted">{currentStep} / {STEPS.length}</div>
                  </div>

                  {renderStepContent()}

                  {/* Navigation */}
                  {currentStep > 1 && currentStep < 8 && (
                    <div className="flex gap-3 mt-6 pt-5 border-t border-border">
                      <button
                        onClick={() => setCurrentStep(s => s - 1)}
                        className="px-4 py-2 text-[13px] font-medium text-ink-muted border border-border rounded-lg hover:bg-surface-2 transition-colors"
                      >
                        ← Back
                      </button>
                      <button
                        onClick={() => setCurrentStep(s => s + 1)}
                        className="flex-1 py-2 text-[13px] font-semibold bg-ai text-white rounded-lg hover:bg-ai-dark transition-colors"
                      >
                        Continue →
                      </button>
                    </div>
                  )}
                  {currentStep === 1 && eligibilityChecked && eligibility?.eligible && (
                    <div className="mt-4" />
                  )}
                  {currentStep > 1 && currentStep === 8 && (
                    <div className="flex gap-3 mt-6 pt-5 border-t border-border">
                      <button
                        onClick={() => setCurrentStep(s => s - 1)}
                        className="px-4 py-2 text-[13px] font-medium text-ink-muted border border-border rounded-lg hover:bg-surface-2 transition-colors"
                      >
                        ← Back
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
